import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  DESKTOP_SCREEN,
  MOBILE_SCREEN,
  useDeviceType,
  useDialogHandler,
} from '@travel/traveler-core/hooks';
import useRatBindClickComponent from '@travel/traveler-core/hooks/useRatBindClickComponent';
import { toSEOSearchString } from '@travel/traveler-core/utils/seo';

import DataLayer from 'components/DataLayer';
import Dialog from 'components/Dialog';
import type {
  InternalSalesPromotionFormQuery,
  SalesPromotionFormQuery,
} from 'components/SalesPromotionInput';
import { PLAN_DETAIL_QUERY } from 'pages/ProviderInformationPage/constants';
import { getExtendedFilteredRoomPlanFeatures } from 'pages/ProviderInformationPage/helpers';

import { pushLocation } from 'store/__router/actions';
import { getRouter } from 'store/__router/selectors';
import { getLimitationsForCurrentMarket } from 'store/markets/selectors';
import { getMember } from 'store/member/selectors';
import { getIsDirectContractType, getProviderFeatures } from 'store/providerInformation/selectors';
import {
  getNoOfAppliedCoupons,
  getNoOfAvailableCoupons,
  getPlanDetail,
} from 'store/providerRatePlan/selectors';

import { RAT_PAGE_TYPE_OBJ } from 'constants/ratAnalytics';
import { OFFER_TYPE } from 'constants/salesPromotion';
import { Translate } from 'core/translate';
import { LinkWithRef } from 'core/universalRouter/Link/LinkWithRef';
import paths from 'core/universalRouter/paths';
import useLogin from 'hooks/useLogin';
import useQueryToggle from 'hooks/useQueryToggle';
import type { Media } from 'ProviderInformation-Types';
import type { RoomFormQuery, SearchFormQuery } from 'ProviderList-Types';
import type { Plan } from 'ProviderRatePlan-Types';
import { splitQueryParamsByAllowedKeys } from 'utils/search';

import PlanCouponItem from '../PlanCouponItem';
import ReserveButton from '../ReserveButton';
import RoomPlanItemCard from '../RoomPlanItemCard';
import PlanDetails from './PlanDetails';

import styles from './roomList.module.scss';

export type Props = {
  /** on what parent page this component lives */
  onPage: 'providerInfoUndated' | 'providerInfoDated';
  /** Display image */
  displayImage?: Media;
  /** Provider id */
  providerId: string;
  /** Plan id */
  planId: string;
  /** Rate plan id */
  ratePlanId: string;
  /** Item plan id */
  itemId: string;
  /** SalesPromotion id */
  appliedSalesPromotionIds?: string[] | null;
  /** Price before discount */
  perRoomPriceBeforeDiscount: number;
  /** Price after discount */
  perRoomPriceAfterDiscount: number | null;
  /** Current search params */
  searchParam: RoomFormQuery & SalesPromotionFormQuery & InternalSalesPromotionFormQuery;
  /** Callback to be called when date is selected */
  onSelectDate: (startDate?: string, endDate?: string) => void;
  /** Search query */
  query: SearchFormQuery;
  /** If detail is opened or not */
  isOpenDetail?: boolean;
};

function PlanItem(props: Props) {
  const {
    searchParam,
    onSelectDate,
    onPage,
    query,
    perRoomPriceBeforeDiscount,
    perRoomPriceAfterDiscount,
  } = props;
  const { isMember } = useSelector(getMember);
  const {
    isOpen: isOpenPlanDetail,
    onClose: onClosePlanDetail,
    onOpen: onDisplayPlanDetail,
  } = useDialogHandler(false);

  const {
    isOpen: isOpenPlanDetailMobile,
    onClose: onClosePlanDetailMobile,
    onOpen: onDisplayPlanDetailMobile,
  } = useQueryToggle(PLAN_DETAIL_QUERY, props.planId);

  const dispatch = useDispatch();
  const deviceType = useDeviceType();
  const isMobile = deviceType === MOBILE_SCREEN;
  const isDesktop = deviceType === DESKTOP_SCREEN;
  const { baseUrl, location } = useSelector(getRouter);
  const limitations = useSelector(getLimitationsForCurrentMarket);
  const isDirectContract = useSelector(getIsDirectContractType);

  const plan: Plan = useSelector(
    getPlanDetail(props.itemId, props.planId, isDirectContract, props.ratePlanId),
  );
  const noOfAppliedCoupons: number = useSelector(
    getNoOfAppliedCoupons(props.itemId, props.planId, isDirectContract, props.ratePlanId),
  );
  const noOfAvailableCoupons: number = useSelector(
    getNoOfAvailableCoupons(props.itemId, props.planId, isDirectContract, props.ratePlanId),
  );

  const { pageType, siteSection, parentName } = RAT_PAGE_TYPE_OBJ[onPage];

  const isCoupon = plan?.price?.salesPromotions?.some((sp) => sp.offerType === OFFER_TYPE.COUPON);

  const isOpenDetail = isMobile ? isOpenPlanDetailMobile : isOpenPlanDetail;

  const handleCloseDetail = isMobile ? onClosePlanDetailMobile : onClosePlanDetail;

  const handleOpenDetail = isMobile ? onDisplayPlanDetailMobile : onDisplayPlanDetail;

  const ratLink = React.useRef<HTMLButtonElement>(null);
  useRatBindClickComponent(ratLink);

  const { onClickLogin: login } = useLogin();

  // reserve button
  const onClickReserveButton = () => {
    ratLink.current?.click();

    /**
     * This param is added to remove app download banner on booking step pages for Kayak users.
     * TODO: [TIDP-63643] remove this param when app download banner is removed for all users
     */
    const currentQueryParams = Object.fromEntries(new URLSearchParams(location.location?.search));
    const { nonAppQueryParams } = splitQueryParamsByAllowedKeys(currentQueryParams);

    const searchString = toSEOSearchString(
      {
        itemId: props.itemId,
        ratePlanId: props.planId,
        itemRatePlanId: props.ratePlanId,
        startDate: searchParam.startDate,
        endDate: searchParam.endDate,
        noOfUnits: searchParam.noOfUnits,
        adults: searchParam.adults,
        childrenAges: searchParam.childrenAges,
        promotionCode: searchParam.promotionCode,
        appliedSalesPromotionIds: props.appliedSalesPromotionIds?.join(','),
        perRoomPriceBeforeDiscount: String(perRoomPriceBeforeDiscount),
        perRoomPriceAfterDiscount: String(perRoomPriceAfterDiscount),
        isCouponSpecified: searchParam.isCouponSpecified,
        ...nonAppQueryParams,
      },
      limitations,
    );

    const bookingLocation = `${paths.bookingStep1.pathResolver(props.providerId)}?${searchString}`;

    if (isMember) {
      dispatch(pushLocation(bookingLocation));
    } else {
      const callbackUrl = `${baseUrl}${bookingLocation}`;
      if (plan?.hasMemberOnly) {
        login(callbackUrl);
      } else {
        dispatch(
          pushLocation(`${paths.loginOrGuest.path}?callback=${encodeURIComponent(callbackUrl)}`),
        );
      }
    }
  };

  // coupon element
  const couponBadge = (
    <PlanCouponItem
      itemId={props.itemId}
      ratePlanId={props.ratePlanId}
      planId={props.planId}
      className={styles.card}
      combinedIncentive={plan?.price.combinedIncentive}
      salesPromotions={plan?.price.salesPromotions}
      priceAfterDiscount={plan?.price.total}
      noOfAppliedCoupons={noOfAppliedCoupons}
      noOfAvailableCoupons={noOfAvailableCoupons}
      query={props.query}
      pageIdentifier={props.providerId}
      parentComponent={'ItemCard'}
    />
  );

  const pageName =
    query?.startDate || query?.endDate
      ? 'hotel:rooms_and_plans_dated_list'
      : 'hotel:rooms_and_plans_undated_list';

  const providerFeatures = useSelector(getProviderFeatures);
  const features = getExtendedFilteredRoomPlanFeatures(
    plan?.hasMeal,
    plan?.cancellationPenalties?.find((item) => item.type === 'NO_PENALTY')?.deadline,
    plan?.facilities,
    0,
    4,
  );

  return (
    <>
      <RoomPlanItemCard
        itemType="plan"
        availabilityCount={plan?.availabilityCount}
        cancellationPenalties={plan?.cancellationPenalties}
        hasMeal={plan?.hasMeal}
        facilities={plan?.facilities}
        media={plan?.media}
        paymentRestrictionFeature={plan?.paymentRestrictionFeature}
        itemTitle={plan?.name}
        price={plan?.price}
        points={plan?.points}
        searchParam={props.searchParam}
        couponBadge={isCoupon && couponBadge}
        onDisplayDetailDialog={handleOpenDetail}
        onReserve={onClickReserveButton}
        onSelectDate={onSelectDate}
        itemRatePlanId={plan.itemRatePlanId}
        isNonRefundable={plan.nonRefundable}
        paymentMethods={plan.paymentMethods}
        cancellationRemarks={plan.cancellationRemarks}
        isRakutenMembership={plan.hasMemberOnly}
      />

      <Dialog
        title={isDesktop ? null : <Translate id="Provider_Info.Room_List.Plan_Details.Title" />}
        isOpen={isOpenDetail}
        isSEOContentIndexedOnHidden={true}
        onClose={handleCloseDetail}
        hasEnableBack={false}
        isUseContentFlexBox={false}
        className={styles.withActionDialog}
        contentClassName={styles.dialogContent}
        hasAnimation={true}
      >
        <div>
          {isDesktop && (
            <h5 className={styles.dialogTitle}>
              <Translate id="Provider_Info.Room_List.Plan_Details.Title" />
            </h5>
          )}
          <PlanDetails
            onPage={onPage}
            itemId={props.itemId}
            planId={props.planId}
            ratePlanId={props.ratePlanId}
            appliedSalesPromotionIds={props.appliedSalesPromotionIds}
            perRoomPriceBeforeDiscount={perRoomPriceBeforeDiscount}
            perRoomPriceAfterDiscount={perRoomPriceAfterDiscount}
            providerId={props.providerId}
            searchParam={props.searchParam}
            displayImage={props.displayImage}
            onSelectDate={onSelectDate}
            query={props.query}
            onReserve={onClickReserveButton}
            isOpenDetail={isOpenDetail}
          />
          {/* Cannot use a built-in button on dialog due to custom event */}
          {isMobile && (
            <div className={styles.actionBox}>
              <ReserveButton
                searchParam={searchParam}
                onClick={onClickReserveButton}
                onSelectDate={onSelectDate}
                isRakutenMembership={plan.hasMemberOnly}
              />
            </div>
          )}
        </div>
      </Dialog>

      {isOpenDetail && (
        <DataLayer
          pageName={`${parentName}:plan_details_popup`}
          pageType={pageType}
          siteSection={siteSection}
        />
      )}
      <button
        className={styles.roomItemRatButton}
        data-ratevent="click"
        data-rat-pgn={pageName}
        data-rat-ssc="hotel"
        data-rat-igenre={providerFeatures.categoryFeatures}
        data-rat-itemid={props.ratePlanId}
        data-rat-shopid={props.providerId}
        data-rat-price={`${perRoomPriceAfterDiscount}`}
        data-rat-itag={features}
        data-rat-cv={`{ 'cart_add': 1 }`}
        ref={ratLink}
      >
        {null}
      </button>
    </>
  );
}

export default React.memo(PlanItem);
